import type { OrgStructure, OrgStructureEdge } from '@commenda/api';
import { getEntityDisplayName } from '@commenda/utils';

import type {
  ChartEntity,
  OwnershipDiagnostic,
  OwnershipModel,
  Relation,
  RelationKind,
} from './ownership-model.types';

class OwnershipModelHelper {
  static readonly FULL_OWNERSHIP = 100;

  static readonly ROLE_SUFFIX: Readonly<Record<RelationKind, string | null>> = {
    equity: null,
    limitedPartner: 'limited partner',
    generalPartner: 'general partner',
    control: null,
  };

  static build(structure: Readonly<OrgStructure>): OwnershipModel {
    const entities: ChartEntity[] = [...structure.entities]
      .sort((a, b) => a.id - b.id)
      .map((entity) => ({
        id: entity.id,
        name: getEntityDisplayName(entity),
        legalFormName: entity.entityLegalFormName,
        isExternal: entity.isNonControlledEntity,
        source: entity,
      }));
    const entitiesById = new Map(entities.map((entity) => [entity.id, entity]));
    const diagnostics: OwnershipDiagnostic[] = [];
    const edgesByPair = new Map<
      string,
      { ownerId: number; ownedId: number; edges: OrgStructureEdge[] }
    >();

    structure.edges.forEach((edge) => {
      const relationId = OwnershipModelHelper.getRelationId(
        edge.fromCorporationId,
        edge.toCorporationId
      );
      if (edge.fromCorporationId === edge.toCorporationId) {
        diagnostics.push({
          code: 'self-ownership',
          entityId: edge.toCorporationId,
          relationId,
        });
        return;
      }
      if (
        !entitiesById.has(edge.fromCorporationId) ||
        !entitiesById.has(edge.toCorporationId)
      ) {
        diagnostics.push({
          code: 'unknown-entity',
          entityId: edge.toCorporationId,
          relationId,
        });
        return;
      }
      const pair = edgesByPair.get(relationId) ?? {
        ownerId: edge.fromCorporationId,
        ownedId: edge.toCorporationId,
        edges: [],
      };
      edgesByPair.set(relationId, { ...pair, edges: [...pair.edges, edge] });
    });

    const relations = [...edgesByPair.entries()]
      .map(([id, pair]) => OwnershipModelHelper.toRelation(id, pair))
      .sort((a, b) => a.ownedId - b.ownedId || a.ownerId - b.ownerId);

    const allocatedByEntity = new Map<number, number>();
    relations.forEach((relation) => {
      allocatedByEntity.set(
        relation.ownedId,
        (allocatedByEntity.get(relation.ownedId) ?? 0) + relation.percent
      );
    });
    allocatedByEntity.forEach((allocated, entityId) => {
      if (allocated > OwnershipModelHelper.FULL_OWNERSHIP + 0.01)
        diagnostics.push({ code: 'over-allocated', entityId });
    });

    return { entities, entitiesById, relations, diagnostics };
  }

  static getRelationId(ownerId: number, ownedId: number): string {
    return `${ownerId}->${ownedId}`;
  }

  // Truncates rather than rounds, so 99.996 never reads as a labelled 100%.
  static formatPercent(percent: number): string {
    const truncated = Math.floor(percent * 100 + 1e-6) / 100;
    return `${Number(truncated.toFixed(2))}%`;
  }

  static getShareLabel(
    relation: Readonly<Pick<Relation, 'percent' | 'kind'>>
  ): string | null {
    const percent = OwnershipModelHelper.formatPercent(relation.percent);
    if (relation.kind === 'control') return `Control · ${percent}`;
    const suffix = OwnershipModelHelper.ROLE_SUFFIX[relation.kind];
    if (suffix) return `${percent} · ${suffix}`;
    return relation.percent < OwnershipModelHelper.FULL_OWNERSHIP
      ? percent
      : null;
  }

  static getStructureHash(model: Readonly<OwnershipModel>): string {
    const entityKey = model.entities
      .map(
        (e) =>
          `${e.id}|${e.name}|${e.legalFormName ?? ''}|${e.isExternal ? 1 : 0}`
      )
      .join(';');
    const relationKey = model.relations
      .map((r) => `${r.id}|${r.percent}|${r.kind}`)
      .join(';');
    const text = `${entityKey}#${relationKey}`;
    let hash = 5381;
    for (let i = 0; i < text.length; i += 1) {
      hash = (hash * 33 + text.charCodeAt(i)) % 4294967296;
    }
    return `${model.entities.length}.${model.relations.length}.${hash.toString(36)}`;
  }

  private static toRelation(
    id: string,
    {
      ownerId,
      ownedId,
      edges,
    }: Readonly<{
      ownerId: number;
      ownedId: number;
      edges: ReadonlyArray<OrgStructureEdge>;
    }>
  ): Relation {
    const percent = edges.reduce(
      (sum, edge) => sum + edge.dilutedOwnershipPercent,
      0
    );
    const roles = new Set(edges.map((edge) => edge.relationshipRole));
    let kind: RelationKind = 'equity';
    if (roles.has('GENERAL_PARTNER')) kind = 'generalPartner';
    else if (roles.has('LIMITED_PARTNER')) kind = 'limitedPartner';
    else if (roles.has('CONTROL') && percent === 0) kind = 'control';
    return {
      id,
      ownerId,
      ownedId,
      percent,
      kind,
      stockClassIds: [
        ...new Set(edges.flatMap((edge) => edge.stockClassIds)),
      ].sort(),
    };
  }
}

export { OwnershipModelHelper };
